/**
 * Marketing Campaigns Page
 * Coming Soon placeholder — campaign persistence and automation workflows
 * are not yet implemented on the backend.
 *
 * Planned features:
 * - Email campaign builder
 * - Push notification campaigns
 * - Automated campaigns (welcome, birthday, win-back)
 * - Customer segmentation targeting
 * - Campaign analytics & A/B testing
 */

import React from 'react';
import { Card } from '../components/ui';

export const MarketingPage: React.FC = () => {
  const plannedFeatures = [
    {
      title: 'Email Campaigns',
      description: 'Design and send beautiful email campaigns to your customers with a drag-and-drop builder.',
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      title: 'Push Notifications',
      description: 'Send targeted push notifications to bring customers back and announce promotions.',
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      ),
    },
    {
      title: 'Customer Segments',
      description: 'Target the right customers with smart segments based on behavior, tier, and purchase history.',
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
    },
    {
      title: 'A/B Testing',
      description: 'Test different subject lines, content, and send times to maximize engagement.',
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
    },
    {
      title: 'Automated Workflows',
      description: 'Set up welcome series, birthday rewards, win-back campaigns, and tier upgrade celebrations that run on autopilot.',
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      ),
    },
    {
      title: 'Campaign Analytics',
      description: 'Track open rates, click-through rates, conversions, and revenue generated from every campaign.',
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-8">
      {/* Header */}
      <div>
        <h1 className="text-h1 font-bold text-black">Marketing</h1>
        <p className="text-base mt-2 text-black-lighter">
          Create campaigns and automate customer communications
        </p>
      </div>

      {/* Hero Section */}
      <Card>
        <div className="px-8 py-16 text-center">
          {/* Illustration */}
          <div className="mx-auto mb-8 w-24 h-24 rounded-full bg-gradient-to-br from-orange-100 to-orange-50 flex items-center justify-center">
            <svg className="w-12 h-12 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
            </svg>
          </div>

          <h2 className="text-h2 font-bold text-black mb-3">
            Marketing Campaigns — Coming Soon
          </h2>
          <p className="text-lg text-black-lighter max-w-2xl mx-auto mb-4">
            We are building powerful marketing tools to help you reach your customers
            with the right message at the right time. Email campaigns, push notifications,
            automated workflows, and detailed analytics are all on the way.
          </p>
          <p className="text-base text-black-lighter max-w-lg mx-auto">
            We will notify you when this feature is available.
          </p>
        </div>
      </Card>

      {/* Planned Features Grid */}
      <div>
        <h3 className="text-h4 font-semibold text-black mb-4">What to Expect</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plannedFeatures.map((feature) => (
            <Card key={feature.title}>
              <div className="p-6">
                <div className="w-12 h-12 rounded-lg bg-orange-50 text-orange-400 flex items-center justify-center mb-4">
                  {feature.icon}
                </div>
                <h4 className="text-base font-semibold text-black mb-2">{feature.title}</h4>
                <p className="text-sm text-black-lighter leading-relaxed">{feature.description}</p>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Bottom CTA */}
      <Card>
        <div className="p-6 flex flex-col sm:flex-row items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-500 flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="text-center sm:text-left">
            <p className="text-sm font-semibold text-black">
              In the meantime, you can send push notifications to individual customers from the Customers page.
            </p>
            <p className="text-sm text-black-lighter mt-1">
              Customer segments are already available and will power targeted campaigns when this feature launches.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default MarketingPage;
